import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  status: 'completed' | 'active' | 'active-loading' | 'active-completed' | 'active-error' | 'not-active';
  text: string;
  color: string;
  /**
   * Zweite Zeile unter dem Schrittnamen. Der Assistent erklärt damit, was gerade
   * passiert („Eigene Adresse in Deutschland"), ohne den Namen zu überladen.
   */
  subText?: string;
}

const StepItem: React.FC<Props> = ({ status, text, color, subText }) => {
  const { classes, cx } = useStyles({ color });

  return (
    <Stack className={classes.stepItem} direction={'row'} alignItems={subText ? 'flex-start' : 'center'} gap={1.75}>
      {status === 'completed' ? (
        <CheckCircleIcon className={cx(classes.icon, status)} />
      ) : status === 'active' ? (
        <CircleIcon className={cx(classes.icon, status)} />
      ) : status === 'active-loading' ? (
        <div className={classes.spinnerBox}>
          <CircularProgress className={classes.spinner} size={26} thickness={4} />
        </div>
      ) : status === 'active-completed' ? (
        <CheckCircleIcon className={cx(classes.icon, status)} />
      ) : status === 'active-error' ? (
        <div className={classes.circleSpacing}>
          <CircleIcon className={cx(classes.icon, status)} />
          <CloseIcon className={classes.close} />
        </div>
      ) : status === 'not-active' ? (
        <CircleIcon className={cx(classes.icon, status)} />
      ) : null}
      <Stack gap={0.25}>
        <Typography className={cx(classes.text, status)}>{text}</Typography>
        {!_.isNil(subText) ? <Typography className={cx(classes.subText, status)}>{subText}</Typography> : null}
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles<{ color: string }>()((theme, { color }) => ({
  stepItem: {},
  circleSpacing: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spinnerBox: {
    width: '36px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: '-4px',
  },
  spinner: {
    color: theme.palette.primary.main,
  },
  icon: {
    fontSize: theme.typography.pxToRem(28),
    color,
    '&.completed': {},
    '&.active, &.active-completed': {
      fontSize: theme.typography.pxToRem(36),
      padding: '2px',
      border: `2px solid ${color}`,
      borderRadius: '50%',
      marginLeft: '-4px',
    },
    '&.active-error': {
      fontSize: theme.typography.pxToRem(36),
      padding: '2px',
      border: `2px solid ${color}`,
      borderRadius: '50%',
      marginLeft: '-4px',
    },
    '&.not-active': {
      fontSize: theme.typography.pxToRem(0),
      padding: '13px',
      border: `2px solid ${color}`,
      borderRadius: '50%',
      marginLeft: '-4px',
      opacity: 0.5,
    },
  },
  close: {
    position: 'absolute',
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.error.main,
    marginRight: '4px',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 1.3,
    color,
    whiteSpace: 'pre-wrap',
    '&.active-loading': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    '&.not-active': {
      opacity: 0.5,
    },
  },
  subText: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.4,
    color: theme.palette.greys.medium,
    '&.not-active': {
      opacity: 0.5,
    },
  },
}));

export default StepItem;
